import type { UploadProps } from 'antd/lib/upload';
import { useNonExpiredAuthCredentials } from './useNonExpiredAuthCredentials';
import { RunwayFetchError } from '@/api';

type UploadRequest = NonNullable<UploadProps['customRequest']>;
type UploadRequestOption = Parameters<UploadRequest>[0];

/**
 * `customRequest` for antd's Upload that refreshes the access token before sending. antd builds its
 * own XHR, so it never goes through runwayFetch and doesn't inherit its refresh-on-401 handling —
 * and re-sending a large screenshot just to learn the token expired is worth avoiding anyway.
 */
export function useAuthedUpload(): UploadRequest {
    const getNonExpiredAuthCredentials = useNonExpiredAuthCredentials();

    // Returns rc-upload's abort handle synchronously so an unmount mid-refresh still cancels
    return ({ action, file, filename, data, method, onProgress, onSuccess, onError }: UploadRequestOption) => {
        let xhr: XMLHttpRequest | null = null;
        let isAborted = false;

        if (!action) {
            onError(new Error('Nothing to upload to'));
            return undefined;
        }

        void getNonExpiredAuthCredentials()
            .then((auth) => {
                if (isAborted) {
                    return;
                }

                const formData = new FormData();
                Object.entries(data ?? {}).forEach(([key, value]) => formData.append(key, value as string));
                formData.append(filename, file);

                xhr = new XMLHttpRequest();

                if (onProgress) {
                    xhr.upload.onprogress = (event) => {
                        onProgress({ percent: event.total > 0 ? (event.loaded / event.total) * 100 : 0 });
                    };
                }

                xhr.onload = () => {
                    const body = parseResponseBody(xhr);

                    if (xhr.status < 200 || xhr.status >= 300) {
                        const error = new Error(xhr.statusText || 'Upload failed') as RunwayFetchError;
                        error.status = xhr.status;

                        onError(error, body);
                        return;
                    }

                    onSuccess(body, xhr);
                };

                xhr.onerror = (event) => onError(event as unknown as Error);

                xhr.open(method ?? 'POST', action, true);
                xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                xhr.setRequestHeader('Authorization', `Bearer ${auth.token}`);
                xhr.send(formData);
            })
            .catch((error) => {
                if (!isAborted) {
                    onError(error);
                }
            });

        return {
            abort() {
                isAborted = true;
                xhr?.abort();
            },
        };
    };
}

const parseResponseBody = (xhr: XMLHttpRequest) => {
    try {
        return JSON.parse(xhr.responseText);
    } catch {
        return xhr.responseText;
    }
};
